import { useState } from 'react';
import { EngineError, createManualCase } from '../../api/engineDashboard';

/** Staff-created case (Danny, 2026-08-24): the mediator already
    coordinated with the parties outside the system. Nothing is sent —
    no confirmations, no invitations, no drips — and an optional
    pre-agreed mediation date books the session directly. */

interface PartyDraft {
  name: string;
  side: 'claimant' | 'respondent';
  email: string;
  phone: string;
}

const emptyParty = (side: 'claimant' | 'respondent'): PartyDraft => ({ name: '', side, email: '', phone: '' });

export function ManualCaseForm({
  token,
  sources,
  onCreated,
  onClose,
}: {
  token: string;
  sources: Array<{ id: string; name: string }>;
  onCreated: (caseId: string) => void;
  onClose: () => void;
}) {
  const [orgId, setOrgId] = useState(sources[0]?.id ?? '');
  const [caseNumber, setCaseNumber] = useState('');
  const [trialDate, setTrialDate] = useState('');
  const [mediationAt, setMediationAt] = useState('');
  const [description, setDescription] = useState('');
  const [sendReminders, setSendReminders] = useState(true);
  const [parties, setParties] = useState<PartyDraft[]>([emptyParty('claimant'), emptyParty('respondent')]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setParty = (i: number, patch: Partial<PartyDraft>) =>
    setParties((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

  const submit = async () => {
    setError(null);
    const filled = parties.filter((p) => p.name.trim());
    if (!orgId) return setError('Pick a source.');
    if (!filled.length) return setError('At least one party name is required.');
    // Safari's datetime-local can hand back strings new Date() cannot
    // parse — toISOString() then THREW before the request ever fired and
    // the form showed the generic failure (Danny, 2026-09-17).
    let mediationIso: string | undefined;
    if (mediationAt) {
      const d = new Date(mediationAt);
      if (isNaN(d.getTime())) {
        return setError('The mediation date and time could not be read. Re-pick it (date AND time) or clear the field.');
      }
      mediationIso = d.toISOString();
    }
    setBusy(true);
    try {
      const res = await createManualCase(token, {
        org_id: orgId,
        case_number: caseNumber.trim() || undefined,
        trial_date: trialDate || undefined,
        mediation_start: mediationIso,
        description: description.trim() || undefined,
        send_reminders: sendReminders,
        parties: filled.map((p) => ({
          name: p.name.trim(),
          side: p.side,
          email: p.email.trim() || undefined,
          phone: p.phone.trim() || undefined,
        })),
      });
      onCreated(res.case_id);
    } catch (err) {
      setError(
        err instanceof EngineError
          ? err.message
          : `Could not create the case (${err instanceof Error ? err.message : 'unknown error'}). Nothing may have been saved. Check the queue before retrying so you do not create a duplicate.`,
      );
      setBusy(false);
    }
  };

  return (
    <section className="card mb-4 p-4">
      <div className="flex items-center justify-between">
        <div className="section-label">New case (coordinated outside the system)</div>
        <button type="button" className="text-xs text-gray-400 hover:text-gray-600" onClick={onClose}>
          ✕ close
        </button>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        Nothing is sent to the parties automatically — no confirmations, invitations, or drips.
        You stay in charge of communication; optional session reminders below are the only exception.
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="block text-xs">
          <span className="font-medium text-gray-600">Source</span>
          <select
            className="mt-0.5 w-full rounded-md border border-surface-border px-2 py-1 text-sm"
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
          >
            {sources.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs">
          <span className="font-medium text-gray-600">Case number (optional)</span>
          <input
            type="text"
            className="mt-0.5 w-full rounded-md border border-surface-border px-2 py-1 text-sm"
            value={caseNumber}
            onChange={(e) => setCaseNumber(e.target.value)}
            placeholder="e.g. 26S00412"
          />
        </label>
        <label className="block text-xs">
          <span className="font-medium text-gray-600">Trial date (optional)</span>
          <input
            type="date"
            className="mt-0.5 w-full rounded-md border border-surface-border px-2 py-1 text-sm"
            value={trialDate}
            onChange={(e) => setTrialDate(e.target.value)}
          />
        </label>
        <label className="block text-xs">
          <span className="font-medium text-gray-600">Mediation date &amp; time (already agreed — optional)</span>
          <input
            type="datetime-local"
            className="mt-0.5 w-full rounded-md border border-surface-border px-2 py-1 text-sm"
            value={mediationAt}
            onChange={(e) => setMediationAt(e.target.value)}
          />
        </label>
      </div>

      <div className="mt-3 text-xs font-medium text-gray-600">Parties</div>
      {parties.map((p, i) => (
        <div key={i} className="mt-1.5 grid gap-2 sm:grid-cols-4">
          <input
            type="text"
            className="rounded-md border border-surface-border px-2 py-1 text-sm"
            placeholder="Full name"
            value={p.name}
            onChange={(e) => setParty(i, { name: e.target.value })}
          />
          <select
            className="rounded-md border border-surface-border px-2 py-1 text-sm"
            value={p.side}
            onChange={(e) => setParty(i, { side: e.target.value as PartyDraft['side'] })}
          >
            <option value="claimant">Plaintiff / Claimant</option>
            <option value="respondent">Defendant / Respondent</option>
          </select>
          <input
            type="email"
            className="rounded-md border border-surface-border px-2 py-1 text-sm"
            placeholder="Email (optional)"
            value={p.email}
            onChange={(e) => setParty(i, { email: e.target.value })}
          />
          <input
            type="tel"
            className="rounded-md border border-surface-border px-2 py-1 text-sm"
            placeholder="Phone (optional)"
            value={p.phone}
            onChange={(e) => setParty(i, { phone: e.target.value })}
          />
        </div>
      ))}
      <button
        type="button"
        className="mt-1.5 text-xs text-brand hover:underline"
        onClick={() => setParties((prev) => [...prev, emptyParty('respondent')])}
      >
        + add another party
      </button>

      <label className="mt-3 flex items-center gap-2 text-xs text-gray-600" title="24h / 2h / 10-minute reminders to parties with contact info — quiet-hours aware. No other automated messaging ever fires on this case.">
        <input type="checkbox" checked={sendReminders} onChange={(e) => setSendReminders(e.target.checked)} />
        Send session reminders to the parties as the mediation approaches
      </label>

      <label className="mt-3 block text-xs">
        <span className="font-medium text-gray-600">What is the dispute about? (optional)</span>
        <textarea
          className="mt-0.5 w-full rounded-md border border-surface-border px-2 py-1 text-sm"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>

      {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
      <div className="mt-3 flex gap-2">
        <button type="button" className="btn-primary !py-1.5 !text-sm" disabled={busy} onClick={() => void submit()}>
          {busy ? 'Creating…' : 'Create case'}
        </button>
        <button type="button" className="btn-secondary !py-1.5 !text-sm" onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </div>
    </section>
  );
}
